import { useState } from 'react';
import { BookOpen, Search, Filter } from 'lucide-react';
import ResearchCard from './ui/ResearchCard';
import MarkdownViewer from './MarkdownViewer';
import { researchDocuments, type ResearchDocument } from '../services/researchService';

export default function Research() {
  const [selectedDoc, setSelectedDoc] = useState<ResearchDocument | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  // Filter documents based on search and category
  const filteredDocuments = researchDocuments.filter(doc => {
    const matchesSearch = doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          doc.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || doc.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // If a document is selected, show the viewer
  if (selectedDoc) {
    return (
      <MarkdownViewer
        documentPath={selectedDoc.filepath}
        documentTitle={selectedDoc.title}
        onBack={() => setSelectedDoc(null)}
      />
    );
  }

  // Otherwise show the research library
  return (
    <div className="space-y-8">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-neutral-900 via-blue-950 to-neutral-900 rounded-3xl p-12 sm:p-16 border border-neutral-800">
        <div className="flex items-start gap-4 mb-4">
          <div className="w-14 h-14 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-2xl flex items-center justify-center flex-shrink-0">
            <BookOpen className="w-7 h-7 text-white" strokeWidth={2.5} />
          </div>
          <div className="flex-1">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">
              Research & Resources
            </h2>
            <p className="text-neutral-300 text-lg leading-relaxed">
              Evidence-based information about AI in occupational therapy, verified and current as of October 2025.
              All research compiled specifically for healthcare practitioners.
            </p>
          </div>
        </div>

        {/* Quick stats */}
        <div className="flex flex-wrap gap-6 mt-8">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
              <BookOpen className="w-4 h-4 text-blue-600" />
            </div>
            <span className="text-sm font-medium text-neutral-300">6 Research Documents</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
              <Search className="w-4 h-4 text-emerald-600" />
            </div>
            <span className="text-sm font-medium text-neutral-300">50,000+ Words</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
              <Filter className="w-4 h-4 text-purple-600" />
            </div>
            <span className="text-sm font-medium text-neutral-300">Verified Oct 2025</span>
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="bg-white rounded-2xl border border-neutral-200 p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
            <input
              type="text"
              placeholder="Search research documents..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Category Filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="pl-10 pr-8 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white cursor-pointer"
            >
              <option value="all">All Categories</option>
              <option value="models">AI Models</option>
              <option value="compliance">Compliance</option>
              <option value="techniques">Techniques</option>
              <option value="applications">Applications</option>
              <option value="evidence">Evidence</option>
            </select>
          </div>
        </div>

        {/* Results count */}
        {(searchQuery || selectedCategory !== 'all') && (
          <p className="text-sm text-neutral-600 mt-3">
            Showing {filteredDocuments.length} of {researchDocuments.length} documents
          </p>
        )}
      </div>

      {/* Featured Quick Access Cards */}
      {!searchQuery && selectedCategory === 'all' && (
        <div>
          <h3 className="text-2xl font-bold text-white mb-4">Quick Access</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {/* Compliance Quick Card */}
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 border border-blue-200 rounded-2xl p-6">
              <h4 className="text-lg font-semibold text-neutral-900 mb-2">🔒 Compliance</h4>
              <p className="text-sm text-neutral-600 mb-4">GDPR & HIPAA requirements</p>
              <div className="space-y-2">
                <button
                  onClick={() => setSelectedDoc(researchDocuments[1])}
                  className="block w-full text-left text-sm text-blue-700 hover:text-blue-800 font-medium"
                >
                  → GDPR (Ireland/EU)
                </button>
                <button
                  onClick={() => setSelectedDoc(researchDocuments[2])}
                  className="block w-full text-left text-sm text-blue-700 hover:text-blue-800 font-medium"
                >
                  → HIPAA (US)
                </button>
              </div>
            </div>

            {/* Getting Started Quick Card */}
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-2xl p-6">
              <h4 className="text-lg font-semibold text-neutral-900 mb-2">🚀 Getting Started</h4>
              <p className="text-sm text-neutral-600 mb-4">Learn AI basics for OT</p>
              <div className="space-y-2">
                <button
                  onClick={() => setSelectedDoc(researchDocuments[0])}
                  className="block w-full text-left text-sm text-emerald-700 hover:text-emerald-800 font-medium"
                >
                  → AI Models Overview
                </button>
                <button
                  onClick={() => setSelectedDoc(researchDocuments[3])}
                  className="block w-full text-left text-sm text-emerald-700 hover:text-emerald-800 font-medium"
                >
                  → Prompting Basics
                </button>
              </div>
            </div>

            {/* Practical Use Quick Card */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200 rounded-2xl p-6">
              <h4 className="text-lg font-semibold text-neutral-900 mb-2">💡 Practical Use</h4>
              <p className="text-sm text-neutral-600 mb-4">Tools & implementation</p>
              <div className="space-y-2">
                <button
                  onClick={() => setSelectedDoc(researchDocuments[4])}
                  className="block w-full text-left text-sm text-purple-700 hover:text-purple-800 font-medium"
                >
                  → AI Applications in OT
                </button>
                <button
                  onClick={() => setSelectedDoc(researchDocuments[5])}
                  className="block w-full text-left text-sm text-purple-700 hover:text-purple-800 font-medium"
                >
                  → Research Evidence
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* All Documents Grid */}
      <div>
        <h3 className="text-2xl font-bold text-white mb-4">
          {searchQuery || selectedCategory !== 'all' ? 'Search Results' : 'All Research Documents'}
        </h3>

        {filteredDocuments.length === 0 ? (
          <div className="bg-neutral-50 rounded-2xl border border-neutral-200 p-12 text-center">
            <Search className="w-12 h-12 text-neutral-400 mx-auto mb-4" />
            <h4 className="text-lg font-medium text-neutral-700 mb-2">No documents found</h4>
            <p className="text-neutral-600 text-sm">
              Try adjusting your search or filter to find what you're looking for
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredDocuments.map((doc) => (
              <ResearchCard
                key={doc.id}
                {...doc}
                onClick={() => setSelectedDoc(doc)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Help Section */}
      <div className="bg-blue-50 border border-blue-200 rounded-2xl p-8">
        <h3 className="text-lg font-semibold text-neutral-900 mb-3">
          💡 New to AI in Healthcare?
        </h3>
        <p className="text-neutral-700 mb-4">
          Start with these recommended documents in order:
        </p>
        <ol className="list-decimal list-inside space-y-2 text-sm text-neutral-700">
          <li>
            <button
              onClick={() => setSelectedDoc(researchDocuments[0])}
              className="text-blue-600 hover:text-blue-700 font-medium underline"
            >
              AI Models Overview
            </button>
            {' '}— Understand current options
          </li>
          <li>
            <button
              onClick={() => setSelectedDoc(researchDocuments[1])}
              className="text-blue-600 hover:text-blue-700 font-medium underline"
            >
              GDPR
            </button>
            {' '}or{' '}
            <button
              onClick={() => setSelectedDoc(researchDocuments[2])}
              className="text-blue-600 hover:text-blue-700 font-medium underline"
            >
              HIPAA Compliance
            </button>
            {' '}— Learn requirements
          </li>
          <li>
            <button
              onClick={() => setSelectedDoc(researchDocuments[3])}
              className="text-blue-600 hover:text-blue-700 font-medium underline"
            >
              Prompting Best Practices
            </button>
            {' '}— Get effective results
          </li>
          <li>
            <button
              onClick={() => setSelectedDoc(researchDocuments[4])}
              className="text-blue-600 hover:text-blue-700 font-medium underline"
            >
              AI Applications in OT
            </button>
            {' '}— Discover practical tools
          </li>
        </ol>
      </div>
    </div>
  );
}
